import { Gift, Sparkles, User, LogOut, Phone, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NotificationAlert } from "@/components/NotificationAlert";
import { buildPublicAppUrl } from "@/lib/appConfig";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const FUN_MESSAGES = [
  "Simplifying group purchases ✨",
  "Friends who buy together, save together 🎁",
  "Group purchases made delightful 🎉",
  "Turning celebrations into memories 💝",
];

interface CustomerHeaderProps {
  userEmail?: string;
  linkedPhone?: string | null;
  onLogout?: () => void;
  onAddPhone?: () => void;
  showMyPurchasesLink?: boolean;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function CustomerHeader({ 
  userEmail, 
  linkedPhone,
  onLogout,
  onAddPhone,
  showMyPurchasesLink = true,
}: CustomerHeaderProps) {
  // Get a consistent message based on the day
  const messageIndex = new Date().getDate() % FUN_MESSAGES.length;
  const funMessage = FUN_MESSAGES[messageIndex];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full border-b border-customer/20 bg-gradient-to-r from-customer/5 via-background to-purple-100/30 dark:to-purple-950/20 backdrop-blur supports-[backdrop-filter]:bg-background/90">
      <div className="container flex h-14 items-center justify-between">
        {/* Left side - Logo + Title */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-customer/10">
              <Gift className="w-4 h-4 text-customer" />
            </div>
            <span className="font-bold text-foreground">Shakatah</span>
          </div>
          <div className="hidden md:block h-5 w-px bg-border" />
          <div className="hidden md:flex items-center gap-1 text-sm font-bold">
            <span className="text-foreground">Group</span>
            <span className="text-pink-500 dark:text-pink-400">Gifts</span>
            <span className="text-muted-foreground font-medium">,</span>
            <span className="text-violet-500 dark:text-violet-400">Purchases</span>
            <span className="text-muted-foreground font-medium">&</span>
            <span className="text-emerald-500 dark:text-emerald-400">Payments</span>
            <span>💫</span>
          </div>
        </div>

        {/* Center - Fun message (hidden on smaller screens) */}
        <div className="hidden xl:flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="w-3 h-3 text-customer" />
          <span className="italic">{funMessage}</span>
        </div>

        {/* Right side - User info, Switcher, Notifications */}
        <div className="flex items-center gap-2">
          {userEmail && (
            <>
              <NotificationAlert userEmail={userEmail} />
              
              {/* User dropdown with email, phone, logout */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-8 px-2 sm:px-3 border-customer/30 bg-customer/5 hover:bg-customer/10"
                  >
                    <User className="w-3.5 h-3.5 text-customer" />
                    <span className="hidden sm:inline ml-1.5 text-xs max-w-[100px] truncate">
                      {userEmail.split('@')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-xs font-medium text-foreground truncate">{userEmail}</p>
                    {linkedPhone && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Phone className="w-3 h-3" />
                        {linkedPhone}
                      </p>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  {!linkedPhone && onAddPhone && (
                    <DropdownMenuItem onClick={onAddPhone} className="text-xs cursor-pointer">
                      <Plus className="w-3.5 h-3.5 mr-2 text-customer" />
                      Add phone number
                    </DropdownMenuItem>
                  )}
                  {onLogout && (
                    <DropdownMenuItem onClick={onLogout} className="text-xs cursor-pointer text-destructive focus:text-destructive">
                      <LogOut className="w-3.5 h-3.5 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
          {!userEmail && showMyPurchasesLink && (
            <Button 
              asChild 
              variant="ghost" 
              size="sm"
              className="text-customer hover:bg-customer/10 h-8 px-2 sm:px-3"
            >
              <a href={buildPublicAppUrl("/my-purchases")}>
                <Gift className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">My Purchases</span>
              </a>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
